import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Checkbox, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { StoryChapterMedia, StoryChapterMediaPaginationFilterRequest } from '@/models/story-chapter-media.model';
import { getPagedStoryChapterMedias } from '@/services/story-chapter-media.service';
import { getStoryChaptersLookup } from '@/services/story-chapter.service';
import ImageCell from '@/components/ImageCell';
import AudioCell from '@/components/AudioCell';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface StoryChapterMediaListPageProps{
  chapterId?: number; 
};

const StoryChapterMediaListPage : React.FC<StoryChapterMediaListPageProps> = ({chapterId}) => {

  const [filterReq, setFilterReq] = useState<StoryChapterMediaPaginationFilterRequest>(new StoryChapterMediaPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<StoryChapterMedia[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: StoryChapterMediaPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {...req, chapterId: chapterId ?? filterReq.chapterId};
      const result: PagedResponseModel<StoryChapterMedia> = await getPagedStoryChapterMedias(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Story Chapters:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/stories/chapters/medias/new`, {state:{chapterId}})}>
          Add Media
        </Button>
        <Stack spacing={6}>             
          {!chapterId && <FetchingSelect
            label="Chapters"
            fetchData={(text, page) => getStoryChaptersLookup(text, page)}
            value={filterReq.chapterId}
            onChange={dataKey => setFilterReq(pre => ({...pre, chapterId: dataKey}))}
          />}
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" sortable>
          <HeaderCell>Order</HeaderCell>
          <Cell dataKey="order" />
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Text</HeaderCell>
          <Cell dataKey="text"/>
        </Column>
        <Column flexGrow={2} align="center">
          <HeaderCell>Voice</HeaderCell>
          <AudioCell dataKey="voiceUrl" />
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Is Intro</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: StoryChapterMedia) => (   
            <Checkbox checked={rowData.isIntro} readOnly></Checkbox>
          )}
          </Cell>
        </Column>        
        <Column flexGrow={0.5} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/stories/chapters/medias/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default StoryChapterMediaListPage;
